import { Controller } from "@hotwired/stimulus"

// Toggles the "⋯" actions dropdown on a document row (predefined or custom).
//
// Markup pattern:
//   <div data-controller="doc-actions">
//     <button data-action="doc-actions#toggle" data-doc-actions-target="button">⋯</button>
//     <div data-doc-actions-target="menu" class="hidden">…actions…</div>
//   </div>
//
// The rows live in a CSS grid, so an absolutely-positioned menu is clipped by
// the neighbouring grid cells. To avoid that, while open the menu is teleported
// to <body> and positioned with `fixed` under the button; it is moved back into
// its original container on close. Closes on outside click, Escape, scroll, and
// resize.
//
// NOTE: Stimulus targets must be descendants of the controller element, so once
// the menu is teleported to <body> the `this.menuTarget` getter would throw
// "Missing target element". We therefore cache a direct DOM reference to the
// menu element (`this._menu`) and reference that everywhere instead.
export default class extends Controller {
  static targets = ["menu", "button"]

  connect() {
    this._onOutsideClick = this._onOutsideClick.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    this._onReposition = this._onReposition.bind(this)
    this._onOtherOpened = this._onOtherOpened.bind(this)
    this._placeholder = null
    // Capture the menu element while it is still in place so we never rely on
    // the Stimulus target getter after teleporting it out of the subtree.
    this._menu = this.hasMenuTarget ? this.menuTarget : null
    // Close this menu whenever another doc-actions menu opens, so only one is
    // ever visible at a time.
    window.addEventListener("doc-actions:open", this._onOtherOpened)
  }

  disconnect() {
    this._teardown()
    window.removeEventListener("doc-actions:open", this._onOtherOpened)
    // Make sure a teleported menu doesn't get orphaned in <body>.
    if (this._placeholder && this._menu) {
      this._placeholder.replaceWith(this._menu)
      this._placeholder = null
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this._menu.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    // Tell any other open menu to close before we open this one.
    window.dispatchEvent(new CustomEvent("doc-actions:open", { detail: { source: this } }))
    this._teleportToBody()
    this._menu.classList.remove("hidden")
    this._position()
    document.addEventListener("click", this._onOutsideClick)
    document.addEventListener("keydown", this._onKeydown)
    window.addEventListener("scroll", this._onReposition, true)
    window.addEventListener("resize", this._onReposition)
  }

  close() {
    this._menu.classList.add("hidden")
    this._restoreFromBody()
    this._teardown()
  }

  _teardown() {
    document.removeEventListener("click", this._onOutsideClick)
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("scroll", this._onReposition, true)
    window.removeEventListener("resize", this._onReposition)
  }

  _teleportToBody() {
    if (this._placeholder) return
    this._placeholder = document.createComment("doc-actions-menu")
    this._menu.replaceWith(this._placeholder)
    document.body.appendChild(this._menu)
  }

  _restoreFromBody() {
    if (!this._placeholder) return
    this._menu.style.top = ""
    this._menu.style.left = ""
    this._menu.style.position = ""
    this._menu.style.width = ""
    this._placeholder.replaceWith(this._menu)
    this._placeholder = null
  }

  _position() {
    const rect = this.buttonTarget.getBoundingClientRect()
    const menu = this._menu
    menu.style.position = "fixed"
    // The compiled Tailwind build resolves `w-40` to a too-narrow width, so set
    // an explicit width here instead of relying on the class.
    menu.style.width = "11rem"
    // Render off-screen first to measure, then clamp into the viewport.
    menu.style.top = "0px"
    menu.style.left = "0px"
    const menuWidth = menu.offsetWidth
    const menuHeight = menu.offsetHeight
    const gap = 4

    // Right-align the menu to the button by default.
    let left = rect.right - menuWidth
    if (left < gap) left = gap
    const maxLeft = window.innerWidth - menuWidth - gap
    if (left > maxLeft) left = maxLeft

    // Open downward; flip above the button if it would overflow the viewport.
    let top = rect.bottom + gap
    if (top + menuHeight > window.innerHeight - gap) {
      const above = rect.top - menuHeight - gap
      if (above >= gap) top = above
    }

    menu.style.left = `${left}px`
    menu.style.top = `${top}px`
  }

  _onReposition() {
    if (!this._menu.classList.contains("hidden")) this._position()
  }

  _onOutsideClick(event) {
    // The menu now lives in <body>, so check both the controller element and
    // the teleported menu.
    if (this.element.contains(event.target)) return
    if (this._menu.contains(event.target)) return
    this.close()
  }

  _onKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  _onOtherOpened(event) {
    // Another menu just opened; close ours unless we are the one opening.
    if (event.detail && event.detail.source === this) return
    if (this._menu && !this._menu.classList.contains("hidden")) this.close()
  }
}
